/**
 * Product File Workspace — real API repository (disk-backed).
 * Uses relative `/api` — session cookie + CSRF via fetch interceptor.
 */

import { ApiError } from '@/services/apiFetch'
import type { WorkspaceRepository } from './repository'
import type { WorkspaceNode, WorkspaceQuota, WorkspaceUploadInput } from './types'

type ApiErrorBody = {
  error?: { code?: string; message?: string; status?: number; details?: Record<string, unknown> }
}

async function parseJson<T>(res: Response): Promise<T> {
  return (await res.json()) as T
}

async function throwIfNotOk(res: Response, fallback: string): Promise<void> {
  if (res.ok) return
  const body = (await res.json().catch(() => null)) as ApiErrorBody | null
  const err = body?.error
  throw new ApiError(
    err?.code ?? 'HTTP_ERROR',
    err?.message ?? `${fallback} (${res.status})`,
    err?.status ?? res.status,
    err?.details,
  )
}

function parentQuery(parentId: string | null): string {
  if (parentId == null) return ''
  return `?parentId=${encodeURIComponent(parentId)}`
}

function basePath(productCode: string, folderSlug: string): string {
  return `/api/workspace/${encodeURIComponent(productCode)}/${encodeURIComponent(folderSlug)}`
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const res = await fetch(dataUrl)
  if (!res.ok) throw new Error('Không đọc được nội dung file để tải lên.')
  return res.blob()
}

export const apiWorkspaceRepository: WorkspaceRepository = {
  async list(productCode, folderSlug, parentId) {
    const res = await fetch(
      `${basePath(productCode, folderSlug)}${parentQuery(parentId)}`,
    )
    await throwIfNotOk(res, 'Không tải được danh sách file')
    const body = await parseJson<{ items: WorkspaceNode[] } & Partial<WorkspaceQuota>>(res)
    return body.items ?? []
  },

  async getQuota() {
    const res = await fetch('/api/workspace/quota')
    await throwIfNotOk(res, 'Không tải được dung lượng lưu trữ')
    return parseJson<WorkspaceQuota>(res)
  },

  async getPath(productCode, folderSlug, nodeId) {
    const q =
      nodeId == null ? '' : `?nodeId=${encodeURIComponent(nodeId)}`
    const res = await fetch(`${basePath(productCode, folderSlug)}/path${q}`)
    await throwIfNotOk(res, 'Không tải được đường dẫn thư mục')
    return parseJson<WorkspaceNode[]>(res)
  },

  async createFolder(productCode, folderSlug, parentId, name) {
    const res = await fetch(`${basePath(productCode, folderSlug)}/folders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parentId, name }),
    })
    await throwIfNotOk(res, 'Không tạo được thư mục')
    return parseJson<WorkspaceNode>(res)
  },

  async uploadFiles(productCode, folderSlug, parentId, files) {
    const form = new FormData()
    if (parentId != null) form.append('parentId', parentId)

    for (const item of files as WorkspaceUploadInput[]) {
      let blob: Blob
      let filename = item.name
      if (item.file) {
        blob = item.file
        if (item.file instanceof File && item.file.name) {
          filename = item.file.name
        }
      } else if (item.dataUrl) {
        blob = await dataUrlToBlob(item.dataUrl)
      } else {
        throw new ApiError('INVALID_FILE', 'Thiếu nội dung file để tải lên.', 400)
      }
      form.append(
        'files',
        blob,
        filename,
      )
    }

    const res = await fetch(`${basePath(productCode, folderSlug)}/upload`, {
      method: 'POST',
      body: form,
    })
    await throwIfNotOk(res, 'Tải lên thất bại')
    return parseJson<WorkspaceNode[]>(res)
  },

  async rename(_productCode, _folderSlug, id, name) {
    const res = await fetch(`/api/workspace/files/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    })
    await throwIfNotOk(res, 'Đổi tên thất bại')
    return parseJson<WorkspaceNode>(res)
  },

  async remove(productCode, folderSlug, ids) {
    const res = await fetch('/api/workspace/files/remove', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productCode, folderSlug, ids }),
    })
    await throwIfNotOk(res, 'Xóa thất bại')
  },

  async move(
    sourceCode,
    sourceSlug,
    ids,
    targetCode,
    targetSlug,
    targetParentId,
  ) {
    const res = await fetch('/api/workspace/files/move', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sourceCode,
        sourceSlug,
        ids,
        targetCode,
        targetSlug,
        targetParentId,
      }),
    })
    await throwIfNotOk(res, 'Di chuyển thất bại')
  },

  async copy(
    sourceCode,
    sourceSlug,
    ids,
    targetCode,
    targetSlug,
    targetParentId,
  ) {
    const res = await fetch('/api/workspace/files/copy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sourceCode,
        sourceSlug,
        ids,
        targetCode,
        targetSlug,
        targetParentId,
      }),
    })
    await throwIfNotOk(res, 'Sao chép thất bại')
    return parseJson<WorkspaceNode[]>(res)
  },
}
